import React, { FC, useState, useEffect } from 'react'
import queryString from 'query-string'
import { useNavigate, useLocation } from 'react-router-dom'
import * as yup from 'yup'
import axios, { AxiosResponse } from 'axios'
import { routes, api, getAuthClient } from 'config'
import {
  constants,
  processErrorMsg,
  removeFocusFromField,
  resetAndExpiredPasswordSchema,
  storage,
} from 'utils'
import { BaseLayout, Loader } from 'components'
import { ResetPasswordForm } from './components'

const ResetPassword: FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [transaction, setTransaction] = useState<any | {}>({})
  const [moduleTitle, setModuleTitle] = useState('User Activation')
  const [showLoader, setShowLoader] = useState(true)
  const [serviceError, setServiceError] = useState('')

  useEffect(() => {
    const params: any = queryString.parse(location.search)
    const isRecoveryToken = Boolean(params.recovery_token)
    const token = params?.recovery_token || params?.activation_token

    if (token) {
      const authClient = getAuthClient()
      isRecoveryToken && setModuleTitle('Reset Password')

      authClient
        .verifyRecoveryToken({ recoveryToken: token })
        .then(processSuccess)
        .catch((error: any) =>
          processError(
            error,
            moduleTitle,
            `Verifying ${
              isRecoveryToken ? 'recovery' : 'activation'
            } token to reset password`,
          ),
        )
    } else {
      navigate(routes.login)
    }
  }, [navigate, location.search, moduleTitle])

  const processSuccess = (response?: any) => {
    storage.setUser(response?.user?.profile?.login)
    setShowLoader(false)
    setTransaction(response)
  }

  const processError = (
    e: any,
    module: string,
    moduleDescription: string = '',
  ) => {
    setShowLoader(false)

    const processedErrorMsg = processErrorMsg(e, module, moduleDescription)

    // inspect error message here and change it if business wanted a customized one
    let displayErrorMsg = processedErrorMsg
    if (
      processedErrorMsg ===
      constants.PROCESSED_OKTA_ERROR_MESSAGE.PASSWORD_CANNOT_BE_CURRENT
    ) {
      displayErrorMsg =
        constants.CUSTOMIZED_OKTA_ERROR_MESSAGE.PASSWORD_CANNOT_BE_CURRENT
      setServiceError(displayErrorMsg)
    } else {
      setServiceError(processErrorMsg(e, module, moduleDescription))
    }
  }

  const handleSubmit = async (values: any, { setSubmitting }: any) => {
    removeFocusFromField()
    setSubmitting(true)

    setServiceError('')
    setShowLoader(true)

    if (
      transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_RESET
    ) {
      try {
        await transaction.resetPassword({
          newPassword: values.newPassword,
        })
      } catch (e) {
        setShowLoader(false)
        setSubmitting(false)
        processError(e, moduleTitle, 'Resetting Okta password')
        return
      }

      try {
        const response: AxiosResponse = await axios.post(api.savePassword(), {
          userId: transaction.user.profile.login,
          newPassword: values.newPassword,
          passwordChangeType: constants.PASSWORD_CHANGE_TYPE.FORGOT_PASSWORD,
        })

        if (response.status === 200) {
          setSubmitting(false)
        } else {
          throw response
        }
      } catch (e) {
        setSubmitting(false)
        processErrorMsg(e, moduleTitle, 'Saving encrypted password')
      }

      /**
       * Cancelling the transaction and redirecting to login
       */
      transaction
        .cancel()
        .then(() => {
          navigate(routes.login, {
            state: {
              message:
                'Your password has been successfully reset. Please log in below.',
            },
          })
          return
        })
        .catch((e: any) =>
          processError(
            e,
            moduleTitle,
            'Password reset successfully and redirecting to login page',
          ),
        )
    }
  }

  const handleCancel = (e: any) => {
    e.preventDefault()

    setServiceError('')
    setShowLoader(true)

    transaction
      .cancel()
      .then(() => {
        setShowLoader(false)
        navigate(routes.login)
      })
      .catch((error: any) =>
        processError(
          error,
          moduleTitle,
          'Cancelling resetting of password and redirecting to login page',
        ),
      )
  }

  if (transaction.sessionToken) {
    return null
  }

  let validationSchema: any
  let initialValues: any

  if (transaction.status === constants.OKTA_TRANSACTION_STATUS.PASSWORD_RESET) {
    initialValues = {
      newPassword: '',
      repeatPassword: '',
    }

    validationSchema = yup
      .object()
      .shape(
        resetAndExpiredPasswordSchema(transaction.policy.complexity.minLength),
      )
  }

  return (
    <BaseLayout>
      <Loader visible={showLoader} />
      <ResetPasswordForm
        isModuleInitialized={Boolean(transaction.status)}
        passwordPolicy={transaction.policy}
        initialValues={initialValues}
        validationSchema={validationSchema}
        handleSubmit={handleSubmit}
        handleCancel={handleCancel}
        serviceError={serviceError}
      />
    </BaseLayout>
  )
}

export default ResetPassword
